import React, { useState, useEffect, useRef } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import {
  StyleSheet,
  Platform,
  Dimensions,
  View,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';

import { useTour } from '../context/TourContext';
import { TourTooltipContent } from '../components/shared';

// Screens
import HomeScreen from '../screens/home/HomeScreen';
import ColourAnalysisScreen from '../screens/color-analysis/ColourAnalysisScreen';
import VirtualDrapeTestScreen from '../screens/virtual-drape/VirtualDrapeTestScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import ColorGenerator from '../components/shared/ColorGenerator';
import ColorLibraryScreen from '../screens/color-library/ColorLibraryScreen';

// Components
import WebLayout from '../components/layout/WebLayout';

// Types
import { BottomTabParamList } from '../types/navigation';
import { useAuth } from '../hooks/useAuth';
import { palette } from '../utils/palette';

const Tab = createBottomTabNavigator<BottomTabParamList>();

// Responsive platform detection
const useResponsiveLayout = () => {
  const getInitialWidth = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return Dimensions.get('window').width;
  };

  const [screenWidth, setScreenWidth] = useState(getInitialWidth);
  const [isWebLayout, setIsWebLayout] = useState(() => {
    const w = getInitialWidth();
    return Platform.OS === 'web' && w >= 768;
  });

  useEffect(() => {
    const updateLayout = () => {
      const width =
        Platform.OS === 'web' && typeof window !== 'undefined'
          ? window.innerWidth
          : Dimensions.get('window').width;
      setScreenWidth(width);
      const shouldUseWebLayout = Platform.OS === 'web' && width >= 768;
      setIsWebLayout(shouldUseWebLayout);
    };

    updateLayout();

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('resize', updateLayout);
      return () => window.removeEventListener('resize', updateLayout);
    }
    const subscription = Dimensions.addEventListener('change', updateLayout);
    return () => subscription?.remove();
  }, []);

  return { screenWidth, isWebLayout };
};

const MobileTabNavigator = ({
  routeParams,
  initialRouteName,
  onTabPress,
}: {
  routeParams?: {
    initialTab?: string;
    section?: string;
    selectedTab?: string;
    fromEdit?: boolean;
    fromLanding?: boolean;
    autoShowAddForm?: boolean;
    virtualDrapeView?: string;
    virtualDrapeViewRequestId?: number;
  };
  initialRouteName?: string | undefined;
  onTabPress?: (tab: string) => void;
}) => {
  const insets = useSafeAreaInsets();
  useAuth();
  const navigation = useNavigation();
  const { tourStep } = useTour();
  const onTabPressRef = useRef(onTabPress);
  onTabPressRef.current = onTabPress;
  const navigatorKey = routeParams?.virtualDrapeViewRequestId
    ? `tabs-${routeParams.virtualDrapeViewRequestId}`
    : 'tabs-default';

  // Track last processed route name to prevent duplicate calls
  const lastProcessedRouteRef = useRef<string | null>(null);
  const hasInitialSyncRef = useRef(false);

  // Listen to navigation state changes to track active tab (for programmatic navigation)
  useEffect(() => {
    if (!onTabPress) return;

    const unsubscribe = navigation.addListener('state', (e) => {
      const state = e.data.state;
      if (state && state.routes && state.index !== undefined) {
        const activeRoute = state.routes[state.index];
        if (activeRoute && activeRoute.name) {
          // Only call onTabPress if the route has actually changed
          if (lastProcessedRouteRef.current !== activeRoute.name) {
            lastProcessedRouteRef.current = activeRoute.name;
            onTabPress(activeRoute.name);
          }
        }
      }
    });

    // Set initial state once on mount only (avoid re-calling onTabPress when effect re-runs due to onTabPress ref change)
    if (!hasInitialSyncRef.current) {
      hasInitialSyncRef.current = true;
      const currentState = navigation.getState();
      if (
        currentState &&
        currentState.routes &&
        currentState.index !== undefined
      ) {
        const activeRoute = currentState.routes[currentState.index];
        if (activeRoute && activeRoute.name) {
          lastProcessedRouteRef.current = activeRoute.name;
          onTabPress(activeRoute.name);
        }
      }
    }

    return unsubscribe;
  }, [navigation, onTabPress]);

  const getInitialRouteName = (): keyof BottomTabParamList => {
    const mobileScreens = [
      'HomeTab',
      'ColourAnalysisMobile',
      'VirtualDrapeTestMobile',
      'ColorGeneratorMobile',
      'ColorLibraryMobile',
      /* 'MoodBoardTabMobile', */ 'ProfileTabMobile',
    ];
    if (
      initialRouteName &&
      mobileScreens.includes(initialRouteName as string)
    ) {
      return initialRouteName as keyof BottomTabParamList;
    }

    if (initialRouteName === 'ProfileTab') return 'ProfileTabMobile';

    // if (initialRouteName === 'MoodBoardTab') return 'MoodBoardTabMobile';

    if (initialRouteName === 'ColourAnalysis') return 'ColourAnalysisMobile';

    if (initialRouteName === 'VirtualDrapeTest')
      return 'VirtualDrapeTestMobile';

    if (initialRouteName === 'ColorGenerator') return 'ColorGeneratorMobile';

    if (initialRouteName === 'ColorLibrary') return 'ColorLibraryMobile';

    return (initialRouteName as keyof BottomTabParamList) || 'HomeTab';
  };

  useEffect(() => {
    if (initialRouteName) {
      const mappedRoute = getInitialRouteName();

      if (mappedRoute && mappedRoute !== 'HomeTab') {
        const currentState = navigation.getState();
        const activeRoute =
          currentState?.routes && currentState.index !== undefined
            ? currentState.routes[currentState.index]
            : null;
        if (activeRoute?.name === mappedRoute) {
          return;
        }

        const timer = setTimeout(() => {
          try {
            navigation.navigate(mappedRoute as never);
          } catch {
            // Navigation might fail if navigator isn't ready yet, ignore
          }
        }, 100);

        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getInitialRouteName is stable, omitting to avoid unnecessary navigator resets
  }, [initialRouteName, navigation]);

  // Tour steps 2–3 render on Home; switch once per step (not on every tab press).
  useEffect(() => {
    if (tourStep < 1) return;

    const timer = setTimeout(() => {
      try {
        const currentState = navigation.getState();
        const currentRoute =
          currentState?.routes && currentState.index !== undefined
            ? currentState.routes[currentState.index]
            : null;
        if (currentRoute?.name !== 'HomeTab') {
          navigation.navigate('HomeTab' as never);
        }
        onTabPressRef.current?.('HomeTab');
      } catch {
        // Navigator may not be ready yet
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [tourStep, navigation]);

  return (
    <Tab.Navigator
      key={navigatorKey}
      initialRouteName={getInitialRouteName() as keyof BottomTabParamList}
      sceneContainerStyle={{
        backgroundColor: palette.panelBackground,
      }}
      screenOptions={{
        headerShown: false,
        tabBarShowLabel: true,
        tabBarStyle: {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          elevation: 8,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: -2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          backgroundColor: palette.white,
          borderTopWidth: 1,
          borderTopColor: palette.veryLightGray,
          height: 65 + insets.bottom,
          paddingBottom: insets.bottom + 5,
          paddingTop: 8,
        },
        tabBarActiveTintColor: '#7C3AED',
        tabBarInactiveTintColor: '#6B7280',
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: '600',
          marginTop: 2,
          lineHeight: 12,
          paddingTop: 0,
        },
        tabBarIconStyle: {
          marginTop: 0,
          marginBottom: 0,
        },
        tabBarItemStyle: {
          paddingVertical: 4,
          paddingHorizontal: 8,
        },
      }}
    >
      <Tab.Screen
        name="HomeTab"
        component={HomeScreen}
        options={{
          tabBarLabel: 'Home',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => {
            return (
              <Ionicons
                name={focused ? 'home' : 'home-outline'}
                color={color}
                size={24}
              />
            );
          },
        }}
        listeners={{
          tabPress: () => {
            navigation.navigate('HomeTab' as never);
            if (onTabPress) {
              lastProcessedRouteRef.current = 'HomeTab';
              onTabPress('HomeTab');
            }
          },
        }}
      />
      <Tab.Screen
        name="ColourAnalysisMobile"
        component={ColourAnalysisScreen}
        options={{
          tabBarLabel: 'Analysis',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => {
            return (
              <Ionicons
                name={focused ? 'color-palette' : 'color-palette-outline'}
                color={color}
                size={24}
              />
            );
          },
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'ColourAnalysisMobile';
              onTabPress('ColourAnalysisMobile');
            }
          },
        }}
      />
      <Tab.Screen
        name="VirtualDrapeTestMobile"
        component={VirtualDrapeTestScreen}
        initialParams={
          routeParams?.virtualDrapeView
            ? {
                initialView: routeParams.virtualDrapeView,
                initialViewRequestId: routeParams.virtualDrapeViewRequestId,
              }
            : undefined
        }
        options={{
          tabBarLabel: 'Drape',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => {
            return (
              <Ionicons
                name={focused ? 'color-wand' : 'color-wand-outline'}
                color={color}
                size={24}
              />
            );
          },
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'VirtualDrapeTestMobile';
              onTabPress('VirtualDrapeTestMobile');
            }
          },
        }}
      />
      <Tab.Screen
        name="ColorGeneratorMobile"
        component={ColorGenerator}
        options={{
          tabBarLabel: 'Generator',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => {
            return (
              <Ionicons
                name={focused ? 'color-filter' : 'color-filter-outline'}
                color={color}
                size={24}
              />
            );
          },
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'ColorGeneratorMobile';
              onTabPress('ColorGeneratorMobile');
            }
          },
        }}
      />
      <Tab.Screen
        name="ColorLibraryMobile"
        component={ColorLibraryScreen}
        options={{
          tabBarLabel: 'Library',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => (
            <Ionicons
              name={focused ? 'library' : 'library-outline'}
              color={color}
              size={24}
            />
          ),
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'ColorLibraryMobile';
              onTabPress('ColorLibraryMobile');
            }
          },
        }}
      />
      {/* Moodboard tab hidden for now
      <Tab.Screen
        name='MoodBoardTabMobile'
        component={MoodBoardScreen}
        initialParams={routeParams}
        options={{
          tabBarLabel: 'Mood Board',
          tabBarIcon: ({ color, focused }: { color: string; focused: boolean; size?: number }) => {
            return <Ionicons name={focused ? 'grid' : 'grid-outline'} color={color} size={24} />;
          },
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'MoodBoardTabMobile';
              onTabPress('MoodBoardTabMobile');
            }
          },
        }}
      />
      */}
      <Tab.Screen
        name="ProfileTabMobile"
        component={ProfileScreen}
        options={{
          tabBarLabel: 'Profile',
          tabBarIcon: ({
            color,
            focused,
          }: {
            color: string;
            focused: boolean;
            size?: number;
          }) => {
            return (
              <Ionicons
                name={focused ? 'person' : 'person-outline'}
                color={color}
                size={24}
              />
            );
          },
        }}
        listeners={{
          tabPress: () => {
            if (onTabPress) {
              lastProcessedRouteRef.current = 'ProfileTabMobile';
              onTabPress('ProfileTabMobile');
            }
          },
        }}
      />
      {/* SmartWardrobe and Settings tabs hidden but not removed */}
      {/* <Tab.Screen 
        name="SmartWardrobeMobile" 
        component={SmartWardrobeScreen} 
        options={{
          tabBarLabel: 'Wardrobe',
          tabBarIcon: ({ color, size }: { color: string; size: number }) => (
            <Ionicons name="shirt-outline" color={color} size={size} />
          ),
        }}
      />
      <Tab.Screen 
        name="SettingsTabMobile" 
        component={SettingsScreen} 
        options={{
          tabBarLabel: 'Settings',
          tabBarIcon: ({ color, size }: { color: string; size: number }) => (
            <Ionicons name="settings-outline" color={color} size={size} />
          ),
        }}
      /> */}
    </Tab.Navigator>
  );
};

interface BottomTabNavigatorProps {
  activeTab?: string;
  onTabPress?: (tab: string) => void;
  routeParams?: {
    initialTab?:
      | 'HomeTab'
      | 'MoodBoardTab'
      | 'ColourAnalysis'
      | 'VirtualDrapeTest'
      | 'SmartWardrobe'
      | 'ProfileTab'
      | 'SettingsTab'
      | 'ColorGenerator';
    section?: string;
    selectedTab?: 'all' | 'saved' | 'my' | 'liked';
    fromEdit?: boolean;
    fromLanding?: boolean;
    autoShowAddForm?: boolean;
  };
}

const BottomTabNavigator: React.FC<BottomTabNavigatorProps> = ({
  activeTab: externalActiveTab,
  onTabPress: externalOnTabPress,
  routeParams: propRouteParams,
}) => {
  const { isWebLayout } = useResponsiveLayout();
  const [internalActiveTab, setInternalActiveTab] = useState('HomeTab');

  const route = useRoute();
  const routeParamsFromRoute = route.params as
    | {
        initialTab?:
          | 'HomeTab'
          | 'MoodBoardTab'
          | 'ColourAnalysis'
          | 'VirtualDrapeTest'
          | 'SmartWardrobe'
          | 'ProfileTab'
          | 'SettingsTab'
          | 'ColorGenerator';
        section?: string;
        selectedTab?: 'all' | 'saved' | 'my' | 'liked';
        fromEdit?: boolean;
        fromLanding?: boolean;
        autoShowAddForm?: boolean;
      }
    | undefined;

  const routeParams = propRouteParams || routeParamsFromRoute;

  const activeTab = externalActiveTab || internalActiveTab;

  const handleTabChange = (tab: string) => {
    setInternalActiveTab(tab);

    if (externalOnTabPress) {
      externalOnTabPress(tab);
    }
  };

  const _setActiveTab = externalOnTabPress || handleTabChange;

  useEffect(() => {
    if (routeParams?.initialTab && !externalActiveTab) {
      const tabToSet =
        !isWebLayout && routeParams.initialTab === 'ProfileTab'
          ? 'ProfileTabMobile'
          : !isWebLayout && routeParams.initialTab === 'ColourAnalysis'
            ? 'ColourAnalysisMobile'
            : !isWebLayout && routeParams.initialTab === 'VirtualDrapeTest'
              ? 'VirtualDrapeTestMobile'
              : !isWebLayout && routeParams.initialTab === 'ColorGenerator'
                ? 'ColorGeneratorMobile'
                : !isWebLayout && routeParams.initialTab === 'ColorLibrary'
                  ? 'ColorLibraryMobile'
                  : routeParams.initialTab;
      // Defer to satisfy react-hooks/set-state-in-effect
      const id = setTimeout(() => setInternalActiveTab(tabToSet), 0);
      return () => clearTimeout(id);
    }
  }, [routeParams?.initialTab, externalActiveTab, isWebLayout]);

  const getMobileInitialTab = (): keyof BottomTabParamList | undefined => {
    if (isWebLayout) return undefined;
    const toMobile: Record<string, keyof BottomTabParamList> = {
      home: 'HomeTab',
      'color-analysis': 'ColourAnalysisMobile',
      'virtual-drape-test': 'VirtualDrapeTestMobile',
      'color-generator': 'ColorGeneratorMobile',
      'color-library': 'ColorLibraryMobile',
      // 'mood-boards': 'MoodBoardTabMobile',
      profile: 'ProfileTabMobile',
      HomeTab: 'HomeTab',
      ColourAnalysis: 'ColourAnalysisMobile',
      VirtualDrapeTest: 'VirtualDrapeTestMobile',
      ColorGenerator: 'ColorGeneratorMobile',
      ColorLibrary: 'ColorLibraryMobile',
      // MoodBoardTab: 'MoodBoardTabMobile',
      ProfileTab: 'ProfileTabMobile',
    };
    const section =
      routeParams?.section && routeParams.section !== 'undefined'
        ? routeParams.section
        : undefined;
    if (section && toMobile[section]) return toMobile[section];
    if (externalActiveTab && toMobile[externalActiveTab])
      return toMobile[externalActiveTab];
    if (routeParams?.initialTab && toMobile[routeParams.initialTab])
      return toMobile[routeParams.initialTab];
    return undefined;
  };

  const { tourStep, totalSteps, currentStepConfig, nextStep, skipTour } =
    useTour();
  const insets = useSafeAreaInsets();
  const tabBarHeight = 65 + insets.bottom + 10;
  const showMobileTour = !isWebLayout && tourStep === 1 && currentStepConfig;

  return isWebLayout ? (
    <WebLayout
      activeTab={activeTab}
      onTabPress={handleTabChange}
      routeParams={routeParams}
    />
  ) : (
    <View style={styles.mobileContainer}>
      <MobileTabNavigator
        routeParams={routeParams}
        initialRouteName={getMobileInitialTab()}
        onTabPress={handleTabChange}
      />
      {showMobileTour && (
        <>
          <Pressable
            style={[styles.mobileTourOverlay, { bottom: tabBarHeight }]}
            onPress={skipTour}
          />
          <View
            style={[styles.mobileTourTabBarHighlight, { height: tabBarHeight }]}
          />
          <View
            style={[
              styles.mobileTourTooltipPosition,
              { bottom: tabBarHeight + 16 },
            ]}
          >
            <TourTooltipContent
              title={currentStepConfig.title}
              onNext={nextStep}
              onSkip={skipTour}
              isLastStep={tourStep === totalSteps}
            />
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mobileContainer: {
    flex: 1,
  },
  mobileTourOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
  },
  mobileTourTabBarHighlight: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2001,
    pointerEvents: 'none',
  },
  mobileTourTooltipPosition: {
    position: 'absolute',
    left: 16,
    right: 16,
    zIndex: 2002,
  },
});

export default BottomTabNavigator;
